import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
	Box,
	Card,
	CardContent,
	Grid2,
	Stack,
	Typography,
	CircularProgress,
} from "@mui/material";
import {
	DashboardSavingsToLoanRatio,
	DashboardCostPerBorrowerIcon,
	DashboardOutstandingLoanBalanceIcon,
} from "../../../../assets";
import { AppLayout } from "../../../../containers/layout/app";
import { ViewCustomerWrapper } from "./styled";
import { retrieveCustomerService } from "../../../../utils/api/customerservice/customerlist/retrieveCustomer";
import Cookies from "universal-cookie";
import { BaseButton } from "../../../../components/button/styled";
import { retrieveSavingsAccountByCustomerIdService } from "../../../../utils/outbound/savingsaccount/retrieveSavingsAccountByCustomerId";
import { formatAmountDisplay } from "../../../../helpers/formatAmountDisplay";
import { useQuery } from "@tanstack/react-query";
import { retrieveUserProfileService } from "../../../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveInstitutionByIdService } from "../../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCurrencyByIdService } from "../../../../utils/outbound/currency/retrieveCurrencyById";
import { retrieveCustomerLoanService } from "../../../../utils/api/loanmanagement/loansetup/retrieveCustomerLoan";

export const ViewCustomer = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { id } = useParams();
	const [customer, setCustomer] = useState({} as Record<string, any>);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!id) return;
		retrieveCustomerService(TOKEN, id)
			.then(async (data) => {
				const customerSavingsAccount =
					await retrieveSavingsAccountByCustomerIdService(TOKEN, data.id);
				const customerLoans = await retrieveCustomerLoanService(TOKEN, data.id);
				const outstandingLoanBalance = (
					Array.isArray(customerLoans) ? customerLoans : []
				).reduce((sum, loan) => {
					const overdueCount = Array.isArray(loan?.overdueRepayments)
						? loan.overdueRepayments.length
						: 0;
					const upcomingCount = Array.isArray(loan?.upcomingRepayments)
						? loan.upcomingRepayments.length
						: 0;
					const partialRepayment = Array.isArray(loan?.partialRepayments)
						? loan.partialRepayments.reduce(
								(sum: number, repayment: any) =>
									sum + Number(repayment?.amountPaid ?? 0),
								0,
							)
						: 0;
					const monthlyInstallment = Number(loan?.monthlyInstallment ?? 0);
					return (
						sum +
						monthlyInstallment * (overdueCount + upcomingCount) -
						partialRepayment
					);
				}, 0);
				setCustomer({
					...data,
					...customerSavingsAccount,
					outstandingLoanBalance,
				});
				return setIsLoading(false);
			})
			.catch((err) => {
				console.error("Failed to fetch customer:", err);
				return setIsLoading(false);
			});
	}, [TOKEN, id]);

	const { data: currency } = useQuery({
		queryKey: ["currencySymbol", TOKEN],
		queryFn: async () => {
			const user = await retrieveUserProfileService(TOKEN);
			const institution = await retrieveInstitutionByIdService(
				user?._id,
				user?.institution,
			);
			const currencyDetails = await retrieveCurrencyByIdService(
				user?._id,
				institution?.currency,
			);
			return currencyDetails?.symbol;
		},
		enabled: !!TOKEN,
	});

	const handleCreateVirtualAccount = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
	};

	return (
		<AppLayout pageId={"Customer-List"} pageTitle={"Customer Details"}>
			<ViewCustomerWrapper>
				<Card
					sx={{
						borderRadius: "1rem",
						border: "1px solid var(--border-diluted-violet-color)",
					}}
				>
					<CardContent className="profile-images card-content">
						<Stack
							direction={"row"}
							height={"100%"}
							padding={"var(--basic-padding)"}
							gap={"calc(var(--flex-gap) / 2)"}
							justifyContent={"center"}
							sx={{
								borderRadius: "0.5rem",
								backgroundColor: "var(--diluted-primary-color)",
							}}
						>
							<Box component={"div"} className="image-box">
								<img src={customer.signature} alt="Customer Signature" />
							</Box>
							<Box component={"div"} className="image-box">
								<img src={customer.passport} alt="Customer Passport" />
							</Box>
							<Box component={"div"} className="image-box">
								<img src={customer.validId} alt="Customer Valid Id" />
							</Box>
						</Stack>
					</CardContent>
				</Card>
				<Card
					sx={{
						borderRadius: "1rem",
						border: "1px solid var(--border-diluted-violet-color)",
					}}
				>
					<CardContent className="profile-information card-content">
						<Typography
							variant="h3"
							fontFamily={"Poppins"}
							fontWeight={700}
							fontSize={20}
							lineHeight={"normal"}
							color="var(--dark-color)"
							marginBlockEnd={"var(--basic-margin)"}
						>
							Profile Information
						</Typography>
						<Stack
							direction={"row"}
							gap={"calc(var(--flex-gap)/4)"}
							marginBlockEnd={"calc(var(--basic-margin)/2)"}
						>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={600}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								Full Name:
							</Typography>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={500}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								{`${customer?.firstName || ""} ${customer?.lastName || ""}`}
							</Typography>
						</Stack>
						<Stack
							direction={"row"}
							gap={"calc(var(--flex-gap)/4)"}
							marginBlockEnd={"calc(var(--basic-margin)/2)"}
						>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={600}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								Phone Number:
							</Typography>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={500}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								{customer?.phone}
							</Typography>
						</Stack>
						<Stack
							direction={"row"}
							gap={"calc(var(--flex-gap)/4)"}
							marginBlockEnd={"calc(var(--basic-margin)/2)"}
						>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={600}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								Email Address:
							</Typography>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={500}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								{customer?.email}
							</Typography>
						</Stack>
						<Stack direction={"row"} gap={"calc(var(--flex-gap)/4)"}>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={600}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								Preffered Contact Method:
							</Typography>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={500}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								{customer?.email && customer?.phone
									? "Email and Phone"
									: customer?.email
										? "Email"
										: customer?.phone
											? "Phone"
											: "Not Available"}
							</Typography>
						</Stack>
					</CardContent>
				</Card>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2 size={{ mobile: 12, tablet: 6 }}>
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"var(--basic-padding)"}
									gap={"calc(var(--flex-gap) * 2)"}
									justifyContent={"space-between"}
								>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={14}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Savings Balance
										</Typography>
									</Box>
									<Stack
										direction={"row"}
										gap={"var(--flex-gap)"}
										alignItems={"center"}
										justifyContent={"space-between"}
										overflow={"hidden"}
									>
										<Box overflow={"hidden"}>
											<Typography
												variant="body1"
												fontFamily={"Poppins"}
												fontWeight={700}
												fontSize={32}
												lineHeight={"normal"}
												color="var(--dark-color)"
											>
												{isLoading ? (
													<CircularProgress
														color="inherit"
														className="loader"
													/>
												) : (
													`${currency || ""}${formatAmountDisplay(customer?.balance ?? 0)}`
												)}
											</Typography>
										</Box>
										<Box overflow={"hidden"}>
											<DashboardOutstandingLoanBalanceIcon />
										</Box>
									</Stack>
								</Stack>
							</CardContent>
						</Card>
					</Grid2>
					<Grid2 size={{ mobile: 12, tablet: 6 }}>
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"var(--basic-padding)"}
									gap={"calc(var(--flex-gap) * 2)"}
									justifyContent={"space-between"}
								>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={14}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Loan Balance
										</Typography>
									</Box>
									<Stack
										direction={"row"}
										gap={"var(--flex-gap)"}
										alignItems={"center"}
										justifyContent={"space-between"}
										overflow={"hidden"}
									>
										<Box overflow={"hidden"}>
											<Typography
												variant="body1"
												fontFamily={"Poppins"}
												fontWeight={700}
												fontSize={32}
												lineHeight={"normal"}
												color="var(--dark-color)"
											>
												{isLoading ? (
													<CircularProgress
														color="inherit"
														className="loader"
													/>
												) : (
													`${currency || ""}${formatAmountDisplay(customer?.outstandingLoanBalance ?? 0)}`
												)}
											</Typography>
										</Box>
										<Box overflow={"hidden"}>
											<DashboardCostPerBorrowerIcon />
										</Box>
									</Stack>
								</Stack>
							</CardContent>
						</Card>
					</Grid2>
					<Grid2 size={{ mobile: 12, tablet: 6 }}>
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"var(--basic-padding)"}
									gap={"calc(var(--flex-gap) * 2)"}
									justifyContent={"space-between"}
								>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={14}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											FTD Balance
										</Typography>
									</Box>
									<Stack
										direction={"row"}
										gap={"var(--flex-gap)"}
										alignItems={"center"}
										justifyContent={"space-between"}
										overflow={"hidden"}
									>
										<Box overflow={"hidden"}>
											<Typography
												variant="body1"
												fontFamily={"Poppins"}
												fontWeight={700}
												fontSize={50}
												lineHeight={"normal"}
												color="var(--dark-color)"
											>
												{isLoading ? (
													<CircularProgress
														color="inherit"
														className="loader"
													/>
												) : (
													`${customer?.ftdBalance ?? 0}`
												)}
											</Typography>
										</Box>
										<Box overflow={"hidden"}>
											<DashboardSavingsToLoanRatio />
										</Box>
									</Stack>
								</Stack>
							</CardContent>
						</Card>
					</Grid2>
					<Grid2 size={{ mobile: 12, tablet: 6 }}>
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 1,
								border: "1px solid var(--border-diluted-violet-color)",
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"var(--basic-padding)"}
									gap={"calc(var(--flex-gap) * 2)"}
									justifyContent={"space-between"}
								>
									<Box>
										<Typography
											variant="h3"
											fontFamily={"Poppins"}
											fontWeight={700}
											fontSize={14}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											Virtual Account Balance
										</Typography>
									</Box>
									<Box
										overflow={"hidden"}
										display={"flex"}
										justifyContent={"center"}
									>
										<Typography
											variant="body1"
											fontFamily={"Inter"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--semi-dark-color)"
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												`${customer?.isVirtualAccount ?? "No virtual account available."}`
											)}
										</Typography>
									</Box>
									<Box
										overflow={"hidden"}
										display={"flex"}
										justifyContent={"center"}
									>
										<BaseButton
											variant="contained"
											bgcolor={"var(--primary-color)"}
											disableElevation
											sx={{
												color: "var(--light-color)",
											}}
											onClick={(e) => handleCreateVirtualAccount(e)}
										>
											<Typography
												variant={"button"}
												fontFamily={"inherit"}
												fontWeight={"inherit"}
												fontSize={"inherit"}
												lineHeight={"inherit"}
												color={"inherit"}
												textTransform={"inherit"}
											>
												Create Virtual Account
											</Typography>
										</BaseButton>
									</Box>
								</Stack>
							</CardContent>
						</Card>
					</Grid2>
				</Grid2>
			</ViewCustomerWrapper>
		</AppLayout>
	);
};
